import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { color, font } from '../styles/theme';
import { shortDate, fullDate } from '../lib/format';
import { useAuth } from '../auth/AuthContext';
import { useMeta, usePeople } from '../api/queries';
import {
  useContacts,
  useContactSummary,
  useTags,
  useBulkContacts,
  useCreateTag,
  isReasonRequired,
  STOP_TEXT_MAX,
  contactTypeLabel,
  contactStageLabel,
  JOURNEY,
  type Contact,
  type ContactFilters,
  type ContactStage,
  type ContactSummary,
} from '../api/contacts';
import {
  PageTitle,
  Card,
  ColHead,
  Loading,
  ErrorNote,
  RedactedBadge,
  RoundButton,
  Button,
  selectStyle,
} from '../components/ui';
import { SectionTabs } from '../components/SectionTabs';
import {
  StagePill,
  RecordTag,
  StaffTag,
  ContactFlags,
  PeopleOptions,
  DUPLICATE_TAG,
  groupRecords,
} from '../components/ContactBits';
import { IconSearch } from '../components/icons';
import { ContactDetail } from './ContactDetail';
import { SubmissionDetail } from './SubmissionDetail';

/**
 * Contacts — every person the lab deals with, and what kind of person each
 * one is.
 *
 * Built like HubSpot's contact index. Each row says who someone is (a type),
 * where they are with the lab (a stage, one per person), and what is in
 * progress (a tag per record, in the same words as Forms submission, the
 * study boards and Messages). All three are worked out by the API from the
 * person's own records, so none of them can go stale; tags are now only what
 * staff put on people (docs/api-contract-contacts.md).
 *
 * The default view is the people the lab works with. More than nine in ten
 * contacts only ever joined the newsletter, and listing them first made the
 * screen say nothing about anyone. They have their own view.
 *
 * One thing deliberately not offered as a bulk action is "add to the
 * newsletter". Subscribing people who never opted in is a CAN-SPAM problem
 * and an open question with Yale counsel; what exists instead is the
 * "Newsletter candidate" tag and a per-contact consent record on the detail.
 *
 * Stop contacting and Clear stop in the bulk bar each ask first, in a strip
 * under it: stopping takes an optional note, kept on each stop; lifting
 * takes a reason, required when any selected stop is one the person asked
 * for themselves (the API refuses the batch whole without it). The owner
 * pickers list everyone with an active account, from `/admins/people`.
 */

/** The bulk stop or lift waiting on its confirmation. */
type StopAsk = 'suppress' | 'unsuppress';

const countPeople = (n: number) => `${n} ${n === 1 ? 'person' : 'people'}`;

const PAGE_SIZE = 25;
const GRID =
  '32px minmax(0,2fr) minmax(96px,0.8fr) minmax(104px,0.8fr) minmax(0,2.6fr) minmax(0,1fr) 88px 40px';

type ViewKey = 'people' | 'active' | 'prospects' | 'clinicians' | 'open' | 'dnc' | 'subscribers' | 'everyone';

/** Saved views across the top, as HubSpot has them. */
const VIEWS: ReadonlyArray<{
  key: ViewKey;
  label: string;
  filters: Partial<ContactFilters>;
  count: (s: ContactSummary) => number;
}> = [
  { key: 'people', label: 'People we work with', filters: { audience: 'people' }, count: (s) => s.people },
  {
    key: 'active',
    label: 'Active participants',
    filters: { stages: 'screening,enrolled' },
    count: (s) => (s.stages.screening ?? 0) + (s.stages.enrolled ?? 0),
  },
  {
    key: 'prospects',
    label: 'Interested & applied',
    filters: { stages: 'interested,applied' },
    count: (s) => (s.stages.interested ?? 0) + (s.stages.applied ?? 0),
  },
  { key: 'clinicians', label: 'Clinicians', filters: { type: 'clinician' }, count: (s) => s.types.clinician ?? 0 },
  { key: 'open', label: 'Open requests', filters: { openMessages: 'true' }, count: (s) => s.openMessages },
  { key: 'dnc', label: 'Do not contact', filters: { suppressed: 'true' }, count: (s) => s.suppressed },
  { key: 'subscribers', label: 'Subscribers', filters: { audience: 'subscribers' }, count: (s) => s.subscribersOnly },
  { key: 'everyone', label: 'Everyone', filters: { audience: 'all' }, count: (s) => s.total },
];

/** Every stage, grouped the way the Stage filter lists them. */
const STAGE_GROUPS: ReadonlyArray<{ label: string; stages: readonly ContactStage[] }> = [
  { label: 'Participant journey', stages: JOURNEY },
  { label: 'Participant endings', stages: ['on-hold', 'not-a-match', 'withdrawn', 'closed'] },
  { label: 'Clinicians', stages: ['asking', 'referring'] },
  { label: 'Messages', stages: ['open-request', 'answered', 'opted-out'] },
  { label: 'Newsletter', stages: ['subscriber', 'unsubscribed', 'none'] },
];

export function Contacts({ search, onSearch }: { search: string; onSearch: (q: string) => void }) {
  const { can } = useAuth();
  const canUpdate = can('submissions:update');
  const meta = useMeta();

  const [view, setView] = useState<ViewKey>('people');
  const [stage, setStage] = useState<string>('');
  const [study, setStudy] = useState<string>('');
  const [owner, setOwner] = useState<string>('');
  const [tag, setTag] = useState<string>('');
  const [sort, setSort] = useState<NonNullable<ContactFilters['sort']>>('recent');
  const [page, setPage] = useState(1);
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [selected, setSelected] = useState<string | null>(null);
  const [openSubmission, setOpenSubmission] = useState<{ id: string; form: string } | null>(null);
  const [bulkTag, setBulkTag] = useState('');
  const [bulkOwner, setBulkOwner] = useState('');
  const [newTag, setNewTag] = useState('');
  const [stopAsk, setStopAsk] = useState<StopAsk | null>(null);
  /** The note on a bulk stop, or the reason for a bulk lift. */
  const [stopText, setStopText] = useState('');

  useEffect(() => {
    setPage(1);
    setChecked({});
  }, [search, view, stage, study, owner, tag, sort]);

  const preset = VIEWS.find((v) => v.key === view)!;
  const filters: ContactFilters = {
    ...preset.filters,
    // A stage picked by hand narrows further than the view's own.
    ...(stage ? { stages: stage } : {}),
    study: study || undefined,
    owner: owner || undefined,
    tag: tag || undefined,
    q: search || undefined,
    sort,
    page,
    pageSize: PAGE_SIZE,
  };

  const list = useContacts(filters);
  const summary = useContactSummary();
  const tags = useTags();
  const staff = usePeople();
  const bulk = useBulkContacts();
  const createTag = useCreateTag();

  const rows = list.data?.rows ?? [];
  const selectedIds = useMemo(() => Object.keys(checked).filter((k) => checked[k]), [checked]);
  // A question about a selection is void once the selection changes.
  useEffect(() => {
    setStopAsk(null);
    setStopText('');
  }, [selectedIds.length]);
  const selectedRows = rows.filter((r) => checked[r.id]);
  const stopped = selectedRows.filter((r) => r.suppressedAt).length;
  const theirStops = selectedRows.filter((r) => r.suppressedAt && r.suppressionSource === 'person').length;
  const allChecked = rows.length > 0 && rows.every((r) => checked[r.id]);
  const tagLabel = (slug: string) => tags.data?.tags.find((t) => t.slug === slug)?.label ?? slug;
  // Staff tags only: the duplicate flag rides on a tag matching sets.
  const staffTags = (tags.data?.tags ?? []).filter((t) => t.slug !== DUPLICATE_TAG);

  function runBulk(action: 'tag' | 'untag' | 'assign' | 'suppress' | 'unsuppress') {
    if (selectedIds.length === 0) return;
    const body: Parameters<typeof bulk.mutate>[0] = { action, ids: selectedIds };
    if (action === 'tag' || action === 'untag') {
      if (!bulkTag) return;
      body.tags = [bulkTag];
    }
    if (action === 'assign') body.ownerId = bulkOwner || null;
    const text = stopText.trim() || undefined;
    if (action === 'suppress') body.note = text;
    if (action === 'unsuppress') body.reason = text;
    bulk.mutate(body, { onSuccess: () => setChecked({}) });
  }

  /** Stop or lift: asked first, in the strip under the bulk bar. */
  function askStop(next: StopAsk) {
    bulk.reset();
    setStopText('');
    setStopAsk(stopAsk === next ? null : next);
  }

  // The API's own refusal says a reason is needed even where the rows did
  // not (a stop that changed hands since the page loaded).
  const reasonNeeded = theirStops > 0 || isReasonRequired(bulk.error);

  function pickView(next: ViewKey) {
    setView(next);
    setStage('');
  }

  const s = summary.data;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }} className="fade-in">
      <PageTitle sub="Who each person is, where they are with the lab, and what is in progress for them.">
        Contacts
      </PageTitle>

      <JourneyFunnel
        summary={s}
        active={stage}
        onPick={(next) => {
          setView('people');
          setStage(stage === next ? '' : next);
        }}
      />

      <SectionTabs
        compact
        value={view}
        onChange={pickView}
        tabs={VIEWS.map((v) => ({ key: v.key, label: v.label, badge: s ? v.count(s) : undefined }))}
      />

      <Card pad={0} style={{ overflow: 'hidden' }}>
        {/* ── Toolbar ── */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
            padding: '14px 20px',
            borderBottom: `1px solid ${color.line}`,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              background: color.bg,
              borderRadius: 999,
              padding: '9px 16px',
              width: 'min(240px,50vw)',
            }}
          >
            <span style={{ color: color.muted, display: 'flex', flex: 'none' }}>
              <IconSearch size={15} />
            </span>
            <input
              value={search}
              onChange={(e) => onSearch(e.target.value)}
              placeholder="Name or email"
              aria-label="Search contacts by name or email"
              style={{ border: 'none', outline: 'none', fontSize: 13, color: color.ink, flex: 1, background: 'transparent', minWidth: 0 }}
            />
          </div>
          <span style={{ fontSize: 13, color: color.muted }}>
            <strong style={{ color: color.ink }}>{list.data?.total ?? 0}</strong> {list.data?.total === 1 ? 'person' : 'people'}
          </span>
          <span style={{ flex: 1 }} />

          <Filter label="Study" value={study} onChange={setStudy}>
            <option value="">any</option>
            {(meta.data?.studies ?? []).map((st) => (
              <option key={st.key} value={st.key}>
                {st.short}
              </option>
            ))}
          </Filter>
          <Filter label="Stage" value={stage} onChange={setStage}>
            <option value="">{preset.filters.stages ? 'as the view' : 'any'}</option>
            {STAGE_GROUPS.map((g) => (
              <optgroup key={g.label} label={g.label}>
                {g.stages.map((k) => (
                  <option key={k} value={k}>
                    {contactStageLabel[k]}
                  </option>
                ))}
              </optgroup>
            ))}
          </Filter>
          <Filter label="Owner" value={owner} onChange={setOwner}>
            <option value="">anyone</option>
            <option value="none">unassigned</option>
            <PeopleOptions people={staff.data?.people} />
          </Filter>
          <Filter label="Tag" value={tag} onChange={setTag}>
            <option value="">any</option>
            {staffTags.map((t) => (
              <option key={t.slug} value={t.slug}>
                {t.label}
              </option>
            ))}
          </Filter>
          <Filter label="Sort" value={sort} onChange={(v) => setSort(v as typeof sort)}>
            <option value="recent">most recent</option>
            <option value="oldest">oldest</option>
            <option value="name">name</option>
            <option value="most-active">most active</option>
          </Filter>
        </div>

        {/* ── Bulk bar: only when something is selected ── */}
        {canUpdate && selectedIds.length > 0 && (
          <div
            className="fade-in"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexWrap: 'wrap',
              padding: '10px 20px',
              background: color.tint,
              borderBottom: `1px solid ${color.line}`,
            }}
          >
            <strong style={{ fontSize: 13, color: color.blueDeep }}>{selectedIds.length} selected</strong>
            <select
              value={bulkTag}
              onChange={(e) => setBulkTag(e.target.value)}
              aria-label="Tag to add or remove"
              style={{ ...selectStyle, width: 'auto', fontSize: 13, padding: '6px 26px 6px 12px' }}
            >
              <option value="">Choose a tag…</option>
              {staffTags.map((t) => (
                <option key={t.slug} value={t.slug}>
                  {t.label}
                </option>
              ))}
            </select>
            <Button disabled={!bulkTag || bulk.isPending} onClick={() => runBulk('tag')}>
              Tag
            </Button>
            <Button disabled={!bulkTag || bulk.isPending} onClick={() => runBulk('untag')}>
              Untag
            </Button>
            <span style={{ width: 1, height: 22, background: color.line }} />
            <select
              value={bulkOwner}
              onChange={(e) => setBulkOwner(e.target.value)}
              aria-label="Owner to assign"
              style={{ ...selectStyle, width: 'auto', fontSize: 13, padding: '6px 26px 6px 12px' }}
            >
              <option value="">Unassigned</option>
              <PeopleOptions people={staff.data?.people} />
            </select>
            <Button disabled={bulk.isPending} onClick={() => runBulk('assign')}>
              Assign
            </Button>
            <span style={{ width: 1, height: 22, background: color.line }} />
            <Button
              disabled={bulk.isPending}
              onClick={() => askStop('suppress')}
              title="You will be asked to confirm"
              style={{ background: color.red }}
            >
              Stop contacting
            </Button>
            <Button
              disabled={bulk.isPending || stopped === 0}
              onClick={() => askStop('unsuppress')}
              title={stopped === 0 ? 'No one selected has a stop' : 'You will be asked to confirm'}
            >
              Clear stop
            </Button>
            <span style={{ flex: 1 }} />
            <button
              onClick={() => setChecked({})}
              style={{ background: 'transparent', border: 'none', color: color.muted, fontSize: 12.5, cursor: 'pointer' }}
            >
              Clear selection
            </button>
          </div>
        )}
        {canUpdate && selectedIds.length > 0 && stopAsk && (
          <StopConfirm
            ask={stopAsk}
            count={stopAsk === 'suppress' ? selectedIds.length : stopped}
            theirs={theirStops}
            reasonNeeded={stopAsk === 'unsuppress' && reasonNeeded}
            text={stopText}
            onText={setStopText}
            pending={bulk.isPending}
            onConfirm={() => runBulk(stopAsk)}
            onCancel={() => {
              setStopAsk(null);
              setStopText('');
              bulk.reset();
            }}
          />
        )}
        {bulk.error && (
          <div style={{ padding: '10px 20px' }}>
            <ErrorNote message={(bulk.error as Error).message} />
          </div>
        )}

        {/* ── Table ── */}
        <div className="x-scroll">
          <div style={{ minWidth: 1040 }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: GRID,
                gap: 12,
                alignItems: 'center',
                padding: '11px 20px',
                background: color.bg,
              }}
            >
              <input
                type="checkbox"
                aria-label="Select all on this page"
                checked={allChecked}
                disabled={!canUpdate}
                onChange={() =>
                  setChecked((c) => {
                    const n = { ...c };
                    rows.forEach((r) => {
                      n[r.id] = !allChecked;
                    });
                    return n;
                  })
                }
                style={{ width: 15, height: 15, accentColor: color.blue, cursor: 'pointer' }}
              />
              <ColHead>Person</ColHead>
              <ColHead>Type</ColHead>
              <ColHead>Stage</ColHead>
              <ColHead>In progress</ColHead>
              <ColHead>Owner</ColHead>
              <ColHead>Last seen</ColHead>
              <span />
            </div>

            {list.isLoading && !list.data ? (
              <Loading />
            ) : list.error ? (
              <ErrorNote message={(list.error as Error).message} />
            ) : rows.length === 0 ? (
              <p style={{ padding: '28px 20px', fontSize: 14, color: color.muted, margin: 0 }}>
                No one matches these filters.
              </p>
            ) : (
              rows.map((r) => (
                <div
                  key={r.id}
                  className="hov-row"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: GRID,
                    gap: 12,
                    alignItems: 'center',
                    padding: '11px 20px',
                    borderBottom: `1px solid ${color.line}`,
                  }}
                >
                  <input
                    type="checkbox"
                    aria-label={`Select ${r.name}`}
                    checked={!!checked[r.id]}
                    disabled={!canUpdate}
                    onChange={() => setChecked((c) => ({ ...c, [r.id]: !c[r.id] }))}
                    style={{ width: 15, height: 15, accentColor: color.blue, cursor: 'pointer' }}
                  />
                  <button
                    onClick={() => setSelected(r.id)}
                    style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', minWidth: 0 }}
                  >
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        fontSize: 13.5,
                        fontWeight: 700,
                        color: color.ink,
                        minWidth: 0,
                      }}
                    >
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.name || 'No name given'}
                      </span>
                      {r.redacted && <RedactedBadge />}
                      <ContactFlags contact={r} />
                    </span>
                    <span
                      style={{
                        display: 'block',
                        fontSize: 11.5,
                        color: color.muted,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {r.redacted ? 'hidden' : (r.email ?? r.phone ?? '—')}
                    </span>
                  </button>
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: color.blueDeep }}>{contactTypeLabel[r.type]}</span>
                  <span>
                    <StagePill stage={r.stage} />
                  </span>
                  <InProgress
                    contact={r}
                    tagLabel={tagLabel}
                    onOpen={(id, form) => setOpenSubmission({ id, form })}
                  />
                  <span
                    style={{
                      fontSize: 12.5,
                      color: r.ownerName ? color.ink : color.muted,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {r.ownerName ?? 'Unassigned'}
                  </span>
                  <span title={fullDate(r.lastSeenAt)} style={{ fontSize: 12.5, color: color.muted, whiteSpace: 'nowrap' }}>
                    {shortDate(r.lastSeenAt)}
                  </span>
                  <button
                    onClick={() => setSelected(r.id)}
                    title="Open their record"
                    aria-label={`Open ${r.name}'s record`}
                    className="hov-tint2"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: color.muted,
                      fontSize: 17,
                      fontWeight: 800,
                      cursor: 'pointer',
                      padding: '4px 8px',
                      borderRadius: 8,
                      lineHeight: 1,
                    }}
                  >
                    ···
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* ── Footer: paging, and a new tag ── */}
        <div
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            flexWrap: 'wrap',
            padding: '12px 20px',
            borderTop: `1px solid ${color.line}`,
          }}
        >
          {canUpdate && (
            <>
              <input
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                placeholder="New tag name"
                aria-label="New tag name"
                style={{ ...selectStyle, backgroundImage: 'none', width: 200, fontSize: 13 }}
              />
              <Button
                disabled={newTag.trim().length === 0 || createTag.isPending}
                onClick={() => createTag.mutate({ label: newTag.trim() }, { onSuccess: () => setNewTag('') })}
              >
                Add tag
              </Button>
              {createTag.error && (
                <span style={{ fontSize: 12.5, color: color.red }}>{(createTag.error as Error).message}</span>
              )}
            </>
          )}
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 12.5, color: color.muted, fontVariantNumeric: 'tabular-nums' }}>
            Page {list.data?.page ?? 1} of {list.data?.pageMax ?? 1}
          </span>
          <RoundButton
            title="Previous page"
            disabled={(list.data?.page ?? 1) <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            ‹
          </RoundButton>
          <RoundButton
            title="Next page"
            disabled={(list.data?.page ?? 1) >= (list.data?.pageMax ?? 1)}
            onClick={() => setPage((p) => p + 1)}
          >
            ›
          </RoundButton>
        </div>
      </Card>

      {selected && (
        <ContactDetail
          id={selected}
          onClose={() => setSelected(null)}
          onOpenSubmission={(id, form) => {
            setSelected(null);
            setOpenSubmission({ id, form });
          }}
        />
      )}
      {openSubmission && <SubmissionDetail id={openSubmission.id} onClose={() => setOpenSubmission(null)} />}
    </div>
  );
}

/**
 * The question a bulk stop or lift asks before it runs, under the bulk bar.
 * A stop takes an optional note; a lift takes a reason, and waits for one
 * when any of the stops is the person's own.
 */
function StopConfirm({
  ask,
  count,
  theirs,
  reasonNeeded,
  text,
  onText,
  pending,
  onConfirm,
  onCancel,
}: {
  ask: StopAsk;
  count: number;
  theirs: number;
  reasonNeeded: boolean;
  text: string;
  onText: (v: string) => void;
  pending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const stop = ask === 'suppress';
  const blocked = pending || (reasonNeeded && text.trim().length === 0);
  return (
    <div
      className="fade-in"
      role="group"
      aria-label={stop ? 'Confirm stop contacting' : 'Confirm lifting the stops'}
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 10,
        flexWrap: 'wrap',
        padding: '10px 20px 12px',
        background: color.tint,
        borderBottom: `1px solid ${color.line}`,
      }}
    >
      <div style={{ flex: '1 1 280px', minWidth: 0 }}>
        <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: color.navy }}>
          {stop ? `Stop contacting ${countPeople(count)}?` : `Lift the stop for ${countPeople(count)}?`}
        </p>
        <p style={{ margin: '2px 0 0', fontSize: 12, lineHeight: '17px', color: color.ink }}>
          {stop
            ? 'Newsletter sends skip them and replies to them are refused until someone lifts the stop.'
            : theirs > 0
              ? `${theirs} of them asked us themselves to stop. Say why the stops are being lifted; the reason stays on each record with your name.`
              : 'Once lifted, the lab can write to them again. A reason is optional.'}
        </p>
      </div>
      <input
        value={text}
        onChange={(e) => onText(e.target.value)}
        maxLength={STOP_TEXT_MAX}
        autoFocus
        aria-label={stop ? 'Note on the stop (optional)' : reasonNeeded ? 'Why the stops are being lifted' : 'Why the stops are being lifted (optional)'}
        placeholder={stop ? 'Note (optional), e.g. asked by phone' : reasonNeeded ? 'Why (required)' : 'Why (optional)'}
        style={{ ...selectStyle, backgroundImage: 'none', width: 260, fontSize: 13, padding: '6px 12px' }}
      />
      <Button
        disabled={blocked}
        onClick={onConfirm}
        title={blocked && !pending ? 'Say why first' : undefined}
        style={stop ? { background: color.red } : undefined}
      >
        {stop ? 'Stop contacting' : 'Lift the stops'}
      </Button>
      <Button variant="ghost" onClick={onCancel} disabled={pending}>
        Cancel
      </Button>
    </div>
  );
}

/** A labelled select in the toolbar. */
function Filter({
  label,
  value,
  onChange,
  children,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  children: ReactNode;
}) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: color.muted, fontWeight: 700 }}>
      {label}:
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...selectStyle, width: 'auto', borderRadius: 999, padding: '6px 26px 6px 12px', fontSize: 12.5 }}
      >
        {children}
      </select>
    </label>
  );
}

/**
 * What is in progress for someone: their live records first, as tags a
 * click opens, and their staff tags after. Someone with nothing live shows
 * their latest finished record, greyed, so a row is never blank about a
 * person who did come to us.
 */
function InProgress({
  contact,
  tagLabel,
  onOpen,
}: {
  contact: Contact;
  tagLabel: (slug: string) => string;
  onOpen: (id: string, form: string) => void;
}) {
  const meta = useMeta();
  if (!meta.data) return <span />;
  const live = groupRecords(contact.records.filter((r) => r.group !== 'closed'));
  const all = groupRecords(contact.records);
  const shown = live.length > 0 ? live.slice(0, 3) : all.slice(0, 1);
  const more = (live.length > 0 ? live.length : all.length) - shown.length;
  const staff = contact.tags.filter((t) => t !== DUPLICATE_TAG);

  return (
    <span style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center', minWidth: 0 }}>
      {shown.map(({ first, count }) => (
        <RecordTag
          key={`${first.submissionId}-${first.via}`}
          meta={meta.data!}
          record={first}
          count={count}
          onOpen={() => onOpen(first.submissionId, first.form)}
        />
      ))}
      {more > 0 && <span style={{ fontSize: 11, fontWeight: 700, color: color.muted }}>+{more}</span>}
      {contact.records.length === 0 && staff.length === 0 && (
        <span style={{ fontSize: 12, color: color.muted }}>
          {contact.newsletter === 'active' ? 'Newsletter only' : '—'}
        </span>
      )}
      {staff.map((t) => (
        <StaffTag key={t}>{tagLabel(t)}</StaffTag>
      ))}
    </span>
  );
}

/**
 * Where people are on the participant journey, left to right. A step is a
 * filter: one click shows who is there.
 */
function JourneyFunnel({
  summary,
  active,
  onPick,
}: {
  summary: ContactSummary | undefined;
  active: string;
  onPick: (stage: ContactStage) => void;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${JOURNEY.length}, minmax(0,1fr))`,
        gap: 0,
        background: color.white,
        border: `1px solid ${color.line}`,
        borderRadius: 18,
        overflow: 'hidden',
      }}
    >
      {JOURNEY.map((k, i) => {
        const on = active === k;
        return (
          <button
            key={k}
            onClick={() => onPick(k)}
            aria-pressed={on}
            className={on ? undefined : 'hov-tint'}
            title={`Show everyone ${contactStageLabel[k].toLowerCase()}`}
            style={{
              textAlign: 'left',
              border: 'none',
              borderLeft: i === 0 ? 'none' : `1px solid ${color.line}`,
              background: on ? color.tint2 : 'transparent',
              padding: '12px 18px',
              cursor: 'pointer',
              minWidth: 0,
            }}
          >
            <span
              style={{
                display: 'block',
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: color.muted,
              }}
            >
              {i > 0 && <span aria-hidden>→ </span>}
              {contactStageLabel[k]}
            </span>
            <span
              style={{
                display: 'block',
                fontFamily: font.display,
                fontWeight: 600,
                fontSize: 24,
                color: color.navy,
                marginTop: 2,
              }}
            >
              {summary ? (summary.stages[k] ?? 0) : '—'}
            </span>
          </button>
        );
      })}
    </div>
  );
}
